import { useState, useRef, useEffect } from 'react';
import {
  Send, UserCheck, Bot, FileText, MessageCircle, CalendarClock, CheckCircle2, Sparkles,
} from 'lucide-react';

type Message = { id: string; from: 'user' | 'ai' | 'firm'; text: string; time: string; forwarded?: boolean };

const seed: Message[] = [
  { id: '1', from: 'ai', text: 'Good morning, Bashir. I can answer questions about your business, taxes or recent records. Where shall we begin?', time: '9:42 AM' },
  { id: '2', from: 'user', text: 'How much GST did I pay last quarter?', time: '9:43 AM' },
  { id: '3', from: 'ai', text: 'Q3 (Jan–Mar 2026): $4,820 in GST — approximately 12% lower than Q2, owing to GST credits on the equipment purchase.', time: '9:43 AM' },
  { id: '4', from: 'user', text: 'Can you help me understand my super contributions?', time: '9:45 AM' },
  { id: '5', from: 'ai', text: 'That is best answered by your accountant directly — I have forwarded your enquiry to Marcus. He will respond shortly.', time: '9:45 AM', forwarded: true },
];

const timelineEvents = [
  { id: 't1', icon: 'message', title: 'Marcus Bell sent correspondence', detail: 'Re: March P&L equipment purchase enquiry', time: 'Today, 14:12' },
  { id: 't2', icon: 'file', title: 'New record filed', detail: 'Tax Return FY2024-25.pdf', time: 'Today, 10:14' },
  { id: 't3', icon: 'done', title: 'Submission acknowledged', detail: 'Equipment receipts received', time: 'Yesterday' },
  { id: 't4', icon: 'deadline', title: 'Q3 BAS reminder', detail: 'Lodgement due in 12 days', time: '2 days prior' },
  { id: 't5', icon: 'message', title: 'Enquiry forwarded', detail: 'Re: superannuation contributions', time: '3 days prior' },
  { id: 't6', icon: 'file', title: 'New record filed', detail: 'Profit & Loss · March 2026', time: '8 April' },
] as const;

export default function HelpPageV3() {
  const [tab, setTab] = useState<'chat' | 'timeline'>('chat');
  const [messages, setMessages] = useState<Message[]>(seed);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tab !== 'chat') return;
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, thinking, tab]);

  const send = (text: string) => {
    if (!text.trim()) return;
    const time = new Date().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
    setMessages(prev => [...prev, { id: String(Date.now()), from: 'user', text: text.trim(), time }]);
    setInput('');
    setThinking(true);
    setTimeout(() => {
      const lower = text.toLowerCase();
      const escalate = lower.includes('super') || lower.includes('audit') || lower.includes('legal');
      setMessages(prev => [...prev, escalate
        ? { id: String(Date.now() + 1), from: 'ai', text: 'That is best answered by Hamid directly — I have forwarded your enquiry.', time, forwarded: true }
        : { id: String(Date.now() + 1), from: 'ai', text: 'Based on your most recent figures, here is what I can see — happy to look further if helpful.', time }]);
      setThinking(false);
    }, 900);
  };

  return (
    <div className="pt-6">
      <div>
        <p className="font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-stone-500">§ 04 · Conversations</p>
        <h2 className="mt-2 font-serif text-3xl font-bold leading-tight tracking-tight">Direct Line</h2>
        <p className="mt-1 text-[12px] uppercase tracking-wider text-stone-500">AI assistance · escalates to your accountant when needed</p>
      </div>

      {/* Editorial tabs */}
      <div className="mt-4 flex gap-0.5 border-b border-stone-300 dark:border-stone-700">
        {[
          { k: 'chat', label: 'Conversation', Icon: MessageCircle },
          { k: 'timeline', label: 'Activity', Icon: CalendarClock },
        ].map(({ k, label, Icon }) => (
          <button
            key={k}
            onClick={() => setTab(k as any)}
            className={`flex items-center gap-1.5 border-b-2 px-3 py-2 font-mono text-[12px] font-bold uppercase tracking-[0.2em] transition-colors ${
              tab === k ? '-mb-px border-teal-700 dark:border-teal-500 text-stone-900 dark:text-stone-100' : '-mb-px border-transparent text-stone-400 dark:text-stone-500 hover:text-stone-700'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'timeline' && (
        <div className="mt-4 pb-4">
          <table className="w-full border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
            <thead>
              <tr className="border-b border-stone-200 dark:border-stone-800 bg-stone-100 dark:bg-stone-800">
                <th className="px-3 py-2 text-left font-mono text-[11px] font-bold uppercase tracking-wider text-stone-500">When</th>
                <th className="px-3 py-2 text-left font-mono text-[11px] font-bold uppercase tracking-wider text-stone-500">Event</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
              {timelineEvents.map(ev => {
                const map = {
                  file: FileText,
                  message: MessageCircle,
                  deadline: CalendarClock,
                  done: CheckCircle2,
                };
                const Icon = map[ev.icon as keyof typeof map];
                return (
                  <tr key={ev.id}>
                    <td className="px-3 py-3 align-top font-mono text-[12px] uppercase tracking-wider text-stone-500">{ev.time}</td>
                    <td className="px-3 py-3 align-top">
                      <div className="flex items-start gap-2">
                        <Icon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-teal-700 dark:text-teal-400" />
                        <div>
                          <p className="text-sm font-semibold text-stone-900 dark:text-stone-100">{ev.title}</p>
                          <p className="mt-0.5 text-[13px] text-stone-600 dark:text-stone-400">{ev.detail}</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'chat' && (
        <>
          {/* Messages flow naturally — bottom padding clears the fixed input bar */}
          <div className="mt-4 space-y-3 pb-40">
            {messages.map(m => <Bubble key={m.id} msg={m} />)}
            {thinking && (
              <div className="flex items-end gap-2">
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900">
                  <Bot className="h-3.5 w-3.5 text-teal-700 dark:text-teal-400" />
                </div>
                <div className="border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 px-3 py-2">
                  <div className="flex gap-1">
                    {[0, 150, 300].map(d => (
                      <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-stone-400" style={{ animationDelay: `${d}ms` }} />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Fixed input bar — pinned to viewport bottom, centred to layout column width */}
          <div className="fixed inset-x-0 bottom-0 z-30 border-t border-stone-300 dark:border-stone-700 bg-stone-50/95 dark:bg-stone-950/95 backdrop-blur-md">
            <div className="mx-auto w-full max-w-[680px] px-5 py-3">
              <form
                onSubmit={e => { e.preventDefault(); send(input); }}
                className="flex items-center gap-2 border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 p-1.5"
              >
                <input
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Type your enquiry…"
                  className="flex-1 bg-transparent px-2 py-2 text-sm placeholder:text-stone-400 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={!input.trim()}
                  className="flex h-9 w-9 items-center justify-center border border-stone-900 dark:border-stone-100 bg-stone-900 dark:bg-stone-100 text-stone-50 hover:bg-stone-800 disabled:border-stone-300 disabled:bg-stone-300"
                >
                  <Send className="h-3.5 w-3.5" />
                </button>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Bubble({ msg }: { msg: Message }) {
  if (msg.from === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[78%] border border-stone-300 dark:border-stone-700 bg-stone-200 dark:bg-stone-800 px-3.5 py-2.5 text-sm text-stone-900 dark:text-stone-100">
          {msg.text}
          <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-stone-500">{msg.time}</p>
        </div>
      </div>
    );
  }
  const isFirm = msg.from === 'firm';
  return (
    <div className="flex items-end gap-2">
      <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center border ${isFirm ? 'border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40' : 'border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900'}`}>
        {isFirm ? <UserCheck className="h-3.5 w-3.5 text-emerald-700 dark:text-emerald-400" /> : <Sparkles className="h-3.5 w-3.5 text-teal-700 dark:text-teal-400" />}
      </div>
      <div className="max-w-[78%]">
        <div className="border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 px-3.5 py-2.5 text-sm text-stone-800 dark:text-stone-200">
          {msg.text}
        </div>
        <div className="mt-1 flex items-center gap-2 px-1">
          <span className="font-mono text-[11px] uppercase tracking-wider text-stone-400 dark:text-stone-500">{msg.time}</span>
          {msg.forwarded && (
            <span className="flex items-center gap-1 border border-emerald-700/30 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 font-mono text-[11px] font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-300">
              <UserCheck className="h-2.5 w-2.5" />
              Forwarded to Hamid
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
